import useNoodleApi from '@hooks/useNoodleApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import * as tsClient from '@tsClient';
import * as ApiModels from '@typings/api-models';
import { useOpenCustomTermsPane, useOpenFormRequestPane, useOpenInvoicePane, useOpenDocumentRequestUserPane } from '@panes/hooks';
import Header from '@components/DesignLibrary/Header';
import TaskList from '@components/TaskList';
import Shimmer from '@components/Shimmer';
import PaginationControls from '@components/DesignLibrary/PaginationControls';
import BannerStatus from '@components/DesignLibrary/BannerStatus';
import ScheduleBookingModal from '@modals/ScheduleBookingModal';
import DashboardOnboardingBanners from '@/components/DashboardOnboardingBanners';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import { useRouter } from 'next/router';
import { useUser } from '@/hooks';
import NoodleIsNowGladeBanner from '@/components/NoodleIsNowGladeBanner';
import useDismissedMessages from '@providers/Messages/useDismissedMessages';

import s from './Home.module.scss';
import TrainingVideos from './TrainingVideos';

type ThisTask = Parameters<typeof TaskList>[0]['tasks'][number] & Pick<ApiModels.Task, 'id' | 'referenceId'>;

type Props = {
  creatorId: string;
};

const PAGE_SIZE = 10;

const UserProfileTaskList: React.FC<Props> = ({ creatorId }) => {
  const [tasks, setTasks] = useState<Array<ThisTask>>([]);
  const [isFetchingPage, setIsFetchingPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const openCustomTermsPane = useOpenCustomTermsPane();
  const openFormRequestPane = useOpenFormRequestPane();
  const openInvoicePane = useOpenInvoicePane();
  const openDocumentRequestUserPane = useOpenDocumentRequestUserPane();
  const { getData: getCustomerTasks } = useNoodleApi(tsClient.my.getCustomerTasks);
  const mostRecentRequestRef = useRef<string | null>(null);
  const [booking, setBooking] = useState<Parameters<typeof ScheduleBookingModal>[0]['booking'] | null>(null);
  const [optedIntoSms, setOptedIntoSms] = useState(false);
  const [personOptedIntoSmsBannerClosed, setPersonOptedIntoSmsBannerClosed] = useState<boolean | undefined>(false);
  const router = useRouter();
  const [user] = useUser();

  const { dismissMessage, isMessageDismissed, isInitialized: isDismissedMessageInitialized } = useDismissedMessages();

  const creatorSlug = router.query.creatorSlug as string;

  const { getData: getUserData } = useNoodleApi(tsClient.getUserInfo);

  const loadTasks = useCallback(
    async ({ page }: { page: number }) => {
      setIsFetchingPage(true);
      const thisRequestId = nanoid();
      mostRecentRequestRef.current = thisRequestId;
      setCurrentPage(page);
      const response = await getCustomerTasks({
        creatorId,
        page,
        perPage: PAGE_SIZE,
      });
      if (response.data && thisRequestId === mostRecentRequestRef.current) {
        setIsFetchingPage(false);
        setTasks(response.data.items);
        setNumPages(response.data.numPages);
      }
    },
    [creatorId, getCustomerTasks],
  );

  const handlePageChange = (newPage: number): void => {
    loadTasks({ page: newPage });
  };

  const handlePersonOptedIntoSmsBannerClose = async (): Promise<void> => {
    dismissMessage(`opt-into-sms-banner-${creatorId}`);
    setPersonOptedIntoSmsBannerClosed(true);
  };

  const handleClickTask = (clickedTask: { id: string }): void => {
    const task = tasks.find(t => t.id === clickedTask.id);
    if (task?.action === 'agree-to-terms') {
      if (task.referenceId) {
        openCustomTermsPane({ customTermsId: task.referenceId });
      }
    } else if (task?.action === 'complete-form-request') {
      if (task.referenceId) {
        openFormRequestPane({ formRequestId: task.referenceId });
      }
    } else if (task?.action === 'pay-invoice') {
      if (task.referenceId) {
        openInvoicePane({ invoiceId: task.referenceId });
      }
    } else if (
      task?.action === 'upload-document-request'
      || task?.action === 'review-document-request'
      || task?.action === 'submit-document-request-for-review'
    ) {
      if (task.referenceId) {
        openDocumentRequestUserPane({ documentRequestUserId: task.referenceId });
      }
    } else if (task?.action === 'schedule-booking') {
      setBooking(task.data.booking);
    } else if (task?.action === 'approve-credit-report' && task.userWorkflow) {
      router.push(getUrl(IDENTIFIERS.USER_PROFILE_ACTIVITY_DETAILS, { creatorSlug, userWorkflowId: task.userWorkflow?.id }));
    }
  };

  const enabledActions = useMemo<Parameters<typeof TaskList>[0]['enabledActions']>(
    () => [
      'agree-to-terms',
      'complete-form-request',
      'pay-invoice',
      'upload-document-request',
      'schedule-booking',
      'review-document-request',
      'submit-document-request-for-review',
      'approve-credit-report',
    ],
    [],
  );

  useEffect(() => {
    loadTasks({
      page: 1,
    });
  }, [loadTasks]);

  useEffect(() => {
    if (user?.id) {
      getUserData().then(({ data }) => {
        if (data?.data) {
          const currentCustomer = data?.data?.customers.find(c => c.creator?.id === creatorId);
          if (currentCustomer) {
            setOptedIntoSms(currentCustomer.optedIntoSms);
          }
        }
      });
    }
    if (isDismissedMessageInitialized) {
      setPersonOptedIntoSmsBannerClosed(isMessageDismissed(`opt-into-sms-banner-${creatorId}`));
    }
  }, [getUserData, user?.id, creatorId, isDismissedMessageInitialized, isMessageDismissed]);

  const hasItems = tasks.length > 0;

  return (
    <div className={s.wrapper}>
      <div className={s.content}>
        <NoodleIsNowGladeBanner />
        <Header title="Your to-do list" />
        <TrainingVideos />
        {isFetchingPage && <Shimmer />}
        {!isFetchingPage && !optedIntoSms && !personOptedIntoSmsBannerClosed && isDismissedMessageInitialized && (
          <DashboardOnboardingBanners
            banners={[
              {
                actions: {
                  primary: {
                    label: 'Click here to opt-in',
                    onClick: () => router.push(getUrl(IDENTIFIERS.USER_PROFILE_EDIT, { creatorSlug })),
                  },
                },
                compact: true,
                description: 'Get timely notifications from your attorney via text.',
                isDismissible: false,
                onDismiss: handlePersonOptedIntoSmsBannerClose,
                title: 'Enable SMS notifications',
              },
            ]}
          />
        )}

        {!isFetchingPage && !hasItems && (
          <>
            <BannerStatus status="success" label={'You have no to-do items yet.'} indent className={s.banner} />
          </>
        )}

        {!isFetchingPage && hasItems && (
          <TaskList tasks={tasks} onClickTask={handleClickTask} title="Your To-Do List" enabledActions={enabledActions} />
        )}

      </div>
      {!isFetchingPage && hasItems && (
        <PaginationControls onChangePage={handlePageChange} page={currentPage} numPages={numPages} className={s.pagination} />
      )}
      {booking && (
        <ScheduleBookingModal
          booking={booking}
          onClose={() => {
            setBooking(null);
          }}
          onSchedule={async () => {
            setTasks(prev => prev.filter(i => !(i.action === 'schedule-booking' && i.referenceId === booking.id)));
          }}
        />
      )}
    </div>
  );
};

export default UserProfileTaskList;
